import { Link, type LinkProps } from "@mui/material";
import type * as React from "react";

import styles from "./LinkButton.module.css";

export interface LinkButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  underline?: LinkProps["underline"];
}

/**
 * A text link that performs an action (renders as <button>) and aligns to
 * the surrounding text baseline. Use inside **prose** — Alerts, paragraphs,
 * descriptions — where the link should inherit the surrounding font and sit
 * on the surrounding text baseline.
 *
 * Not the right fit for standalone or layout-positioned link-buttons that
 * need their own typography (custom color, font size, weight). Those should
 * keep using `<Link component="button">` directly. Examples in this codebase:
 * `ShowHiddenToggle`, `EventTime`, `DatasetInfo`.
 *
 * @example
 * ```tsx
 * <Alert severity="info">
 *   Select a file from the{" "}
 *   <LinkButton onClick={goToFiles}>Files tab</LinkButton>{" "}
 *   to view its topics.
 * </Alert>
 * ```
 */
export function LinkButton({
  className,
  underline = "hover",
  ...props
}: LinkButtonProps) {
  return (
    <Link
      component="button"
      type="button"
      underline={underline}
      className={className ? `${styles.link} ${className}` : styles.link}
      {...props}
    />
  );
}
